import { ComponentType, ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { ChevronLeft, X, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarInitials } from '@/components/ui/avatar';

/*
 * Sidebar — DESIGN_SYSTEM.md §2.3
 *
 * App-shell sidebar. 240px expanded, 56px collapsed, dark surface
 * (`bg-ink`). Composed of:
 *   <Sidebar>
 *     <Sidebar.Brand href="/dashboard" logo={…} />
 *     <Sidebar.Nav>
 *       <Sidebar.Item href="/x" icon={Calendar} label="…" />
 *       <Sidebar.Section label="Admin" />
 *       <Sidebar.Item ... />
 *     </Sidebar.Nav>
 *     <Sidebar.Foot user={…} onLogout={…} />
 *   </Sidebar>
 *
 * The container controls the open/collapsed state and exposes it to
 * children via context.
 */

import { createContext, useContext } from 'react';

interface SidebarContextValue {
  /** Mobile drawer open. */
  open: boolean;
  /** Desktop: rail (icon-only) mode. */
  collapsed: boolean;
  /** Close handler — used by Items to dismiss the mobile drawer on click. */
  onCloseDrawer?: () => void;
}

const SidebarContext = createContext<SidebarContextValue>({
  open: false,
  collapsed: false,
});

function useSidebar() {
  return useContext(SidebarContext);
}

interface SidebarProps {
  open: boolean;
  collapsed: boolean;
  onCloseDrawer?: () => void;
  onToggleCollapse?: () => void;
  children: ReactNode;
  className?: string;
}

export function Sidebar({
  open,
  collapsed,
  onCloseDrawer,
  onToggleCollapse,
  children,
  className,
}: SidebarProps) {
  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-ink/75 lg:hidden"
          onClick={onCloseDrawer}
          aria-hidden
        />
      )}

      <SidebarContext.Provider value={{ open, collapsed, onCloseDrawer }}>
        <aside
          className={cn(
            'fixed inset-y-0 z-50 flex flex-col bg-ink text-white transition-all duration-300',
            open ? 'left-0' : '-left-64',
            'lg:left-0',
            collapsed ? 'lg:w-14' : 'lg:w-60',
            'w-60',
            className,
          )}
        >
          {children}
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="absolute top-4 right-4 hidden text-soft hover:text-white lg:block"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft
                className={cn('h-5 w-5 transition-transform', collapsed && 'rotate-180')}
              />
            </button>
          )}
        </aside>
      </SidebarContext.Provider>
    </>
  );
}

interface SidebarBrandProps {
  href: string;
  logoSrc: string;
  alt: string;
}

function SidebarBrand({ href, logoSrc, alt }: SidebarBrandProps) {
  const { collapsed, onCloseDrawer } = useSidebar();
  return (
    <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-4">
      {!collapsed && (
        <Link href={href} className="flex items-center" onClick={onCloseDrawer}>
          <img src={logoSrc} alt={alt} className="h-8" />
        </Link>
      )}
      <button
        type="button"
        onClick={onCloseDrawer}
        className="text-soft hover:text-white lg:hidden"
        aria-label="Close menu"
      >
        <X className="h-6 w-6" />
      </button>
    </div>
  );
}

interface SidebarNavProps {
  children: ReactNode;
  className?: string;
}

/**
 * Holds the nav plus anything below it (e.g. an account section). On phones
 * and short screens the whole body scrolls as one list; on tall desktop
 * screens only the nav scrolls and the rest stays pinned above the foot.
 */
function SidebarBody({ children, className }: SidebarNavProps) {
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-y-auto',
        'lg:overflow-hidden lg:[@media(max-height:760px)]:overflow-y-auto',
        className,
      )}
    >
      {children}
    </div>
  );
}

function SidebarNav({ children, className }: SidebarNavProps) {
  return (
    <nav
      className={cn(
        'shrink-0 space-y-1 px-2 py-2',
        'lg:min-h-0 lg:flex-1 lg:shrink lg:overflow-y-auto lg:py-4',
        'lg:[@media(max-height:760px)]:flex-none lg:[@media(max-height:760px)]:overflow-visible lg:[@media(max-height:760px)]:py-2',
        className,
      )}
    >
      {children}
    </nav>
  );
}

interface SidebarSectionProps {
  label: ReactNode;
}

function SidebarSection({ label }: SidebarSectionProps) {
  const { collapsed } = useSidebar();
  return (
    <div className="px-2 py-1.5 lg:py-3 lg:[@media(max-height:760px)]:py-1.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 border-t border-white/10" />
        {!collapsed && (
          <span className="text-mono-eyebrow text-soft">{label}</span>
        )}
        <div className="flex-1 border-t border-white/10" />
      </div>
    </div>
  );
}

interface SidebarItemProps {
  href: string;
  icon: ComponentType<{ className?: string }>;
  label: ReactNode;
  /** DOM id for onboarding-tour anchors etc. */
  id?: string;
  /** Optional trailing badge (count, dot, etc.). */
  trailing?: ReactNode;
  /** Also active on sub-pages (e.g. /admin/sessions/notes/:id for /admin/sessions). */
  matchPrefix?: boolean;
}

function SidebarItem({ href, icon: Icon, label, id, trailing, matchPrefix }: SidebarItemProps) {
  const [location] = useLocation();
  const { collapsed, onCloseDrawer } = useSidebar();
  const isActive = location === href || (matchPrefix === true && location.startsWith(`${href}/`));
  return (
    <Link
      href={href}
      id={id}
      onClick={onCloseDrawer}
      className={cn(
        'group flex items-center rounded-md px-2 py-2 text-[13px] font-medium leading-tight tracking-[0.01em] transition-colors',
        isActive
          ? 'bg-brand-500 text-white'
          : 'text-white/75 hover:bg-white/5 hover:text-white',
      )}
    >
      <Icon
        className={cn(
          'h-5 w-5 shrink-0',
          isActive ? 'text-white' : 'text-white/75 group-hover:text-white',
          collapsed ? 'mx-auto' : 'mr-3',
        )}
      />
      {!collapsed && (
        <span className="flex-1 truncate">{label}</span>
      )}
      {!collapsed && trailing}
    </Link>
  );
}

interface SidebarFootProps {
  profileHref?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  avatarUrl?: string;
  onLogout?: () => void;
  logoutLabel?: string;
}

function SidebarFoot({
  profileHref = '/profile',
  firstName,
  lastName,
  role,
  avatarUrl,
  onLogout,
  logoutLabel = 'Sign out',
}: SidebarFootProps) {
  const { collapsed, onCloseDrawer } = useSidebar();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const id = `sidebar-user-${firstName ?? ''}-${lastName ?? ''}`;

  if (collapsed) {
    return (
      <div className="flex justify-center border-t border-white/10 p-4">
        <button
          type="button"
          onClick={onLogout}
          className="text-soft hover:text-white"
          title={logoutLabel}
          aria-label={logoutLabel}
        >
          <LogOut className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between border-t border-white/10 p-4">
      <Link
        href={profileHref}
        onClick={onCloseDrawer}
        className="-m-1 flex items-center rounded-lg p-1 transition-colors hover:bg-white/5"
      >
        <Avatar size="sm">
          {avatarUrl ? (
            <AvatarImage src={avatarUrl} alt={fullName} />
          ) : (
            <AvatarInitials id={id} name={fullName || 'U'} />
          )}
        </Avatar>
        <div className="ml-3 min-w-0">
          <p className="truncate text-[13px] font-medium text-white">{fullName}</p>
          {role && <p className="text-[11px] text-soft capitalize">{role}</p>}
        </div>
      </Link>
      {onLogout && (
        <button
          type="button"
          onClick={onLogout}
          className="text-soft hover:text-white"
          title={logoutLabel}
          aria-label={logoutLabel}
        >
          <LogOut className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

Sidebar.Brand = SidebarBrand;
Sidebar.Body = SidebarBody;
Sidebar.Nav = SidebarNav;
Sidebar.Section = SidebarSection;
Sidebar.Item = SidebarItem;
Sidebar.Foot = SidebarFoot;
